
// Core training types - Frontend models (camelCase, required fields)
export type TrainingType = 'bjj' | 'wrestling' | 'judo' | 'mma';
export type RollType = 'gi' | 'nogi' | 'both';
export type Intensity = 'light' | 'moderate' | 'hard' | 'competition';
export type TimePeriod = 'day' | 'week' | 'month' | 'year' | 'all';

// Session types for frontend forms and display
export type SessionType = 'Gi' | 'No-Gi' | 'Competition' | 'Wrestling' | 'Open Mat' | 'Private Lesson' | 'Seminar' | 'Other';

export interface Technique {
  id: string;
  name: string;
  position?: string;
  type?: string;
}

export interface SessionTechnique {
  id: string;
  name: string;
  notes?: string;
}

// Frontend training session model - camelCase, required fields
export interface TrainingSession {
  id: string;
  userId: string;
  title: string;
  date: string;
  duration: number;
  sessionType?: SessionType;
  professor?: string;
  techniques?: string[];
  location?: string;
  notes?: string;
  learned?: string;
  improvements?: string;
  successes?: string;
  youtubeUrl?: string;
  beltRank?: string;
  stripeCount?: string;
  createdAt: string;
  updatedAt: string;
  // Competition specific fields
  competitionTotalMatches?: number;
  competitionWeightClasses?: string;
  competitionTotalWins?: number;
  competitionFinish?: string;
  competitionMatches?: CompetitionMatch[];
}

// Form submission type - what the form actually sends
export interface TrainingFormValues {
  sessionType?: SessionType;
  date: Date;
  duration?: number;
  professor?: string;
  techniques?: string[] | string;
  location?: string;
  notes?: string;
  learned?: string;
  improvements?: string;
  successes?: string;
  youtube_url?: string;
  belt_rank?: string;
  stripe_count?: string;
  training_type?: TrainingType;
  roll_type?: RollType;
  intensity?: Intensity;
  teammates?: string;
  // Competition specific fields
  competition_total_matches?: number;
  competition_weight_classes?: string;
  competition_total_wins?: number;
  competition_finish?: string;
  competition_matches?: CompetitionMatch[];
}

// Competition match type
export interface CompetitionMatch {
  id?: string;
  gi_nogi: 'gi' | 'nogi';
  weight_class: string;
  result: 'win' | 'loss';
  method: 'points' | 'submission' | 'dq';
  video_url?: string;
  division: 'Child' | 'Juvenile 1' | 'Juvenile 2' | 'Adult' | 'Absolute' | 'Masters 1' | 'Masters 2' | 'Masters 3' | 'Masters 4' | 'Masters 5' | 'Masters 6' | 'Other';
}

// Statistics interfaces
export interface SessionStats {
  totalSessions: number;
  totalDuration: number;
  averageDuration: number;
}

export interface PerformanceMetrics {
  improvement: number;
  consistency: number;
  progress: number;
}

export interface WeeklyStats {
  week: string;
  sessions: number;
  duration: number;
}

export interface MonthlyStats {
  month: string;
  sessions: number;
  duration: number;
}

export interface ChartDataPoint {
  name: string;
  current: number;
  previous: number;
}

export interface DayData {
  day: string;
  dayLabel: string;
  count: number;
  duration: number;
}

export interface PeriodBoundaries {
  currentStart: Date;
  currentEnd: Date;
  previousStart: Date;
  previousEnd: Date;
}

export interface SessionWithParsedDate extends TrainingSession {
  parsedDate: Date;
}

// Storage keys for localStorage
export const STORAGE_KEYS = {
  SESSIONS: 'training-sessions',
  TIME_PERIOD: 'selected-time-period',
  PROFESSOR: 'last-used-professor',
  FORM_DRAFT: 'training-form-draft',
  USER_HOME_GYM: 'user-home-gym'
} as const;

// Context type definitions
export interface TrainingJournalContextType {
  sessions: TrainingSession[];
  selectedTimePeriod: TimePeriod;
  isLoading: boolean;
  error: string | null;
  refreshSessions: () => Promise<void>;
  addSession: (formValues: TrainingFormValues) => Promise<TrainingSession | null>;
  editSession: (sessionId: string, formValues: TrainingFormValues) => Promise<TrainingSession | null>;
  deleteSession: (sessionId: string) => Promise<void>;
  clearSessions: () => Promise<void>;
  setSelectedTimePeriod: (timePeriod: TimePeriod) => void;
  loadStoredData?: () => Promise<TrainingSession[]>;
}

// Query test types
export interface QueryTestDetails {
  test: string;
  planDuration: number;
  queryDuration: number;
  rowCount: number;
  totalCount: number;
  plan: unknown;
  error?: string;
}

export interface QueryTestResult {
  results: QueryTestDetails[];
  executionTime: number;
}
